import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import darkThemeConfig from '../../style/darkTheme';
import themeConfig from '../../style/theme';

type ThemeContextType = {
    isDarkMode: boolean;
    toggleTheme: () => void;
    baseFontSize: number;
    setBaseFontSize: Dispatch<SetStateAction<number>>;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { value: storedTheme, setValue: setStoredTheme } = useLocalStorage('theme', 'dark');
    const { value: baseFontSize, setValue: setBaseFontSize } = useLocalStorage('baseFontSize', 1);
    const [isDarkMode, setIsDarkMode] = useState(() => storedTheme === 'dark');

    const currentThemeConfig = isDarkMode ? darkThemeConfig : themeConfig;
    const theme = createTheme({
        ...currentThemeConfig,
        typography: {
            ...getTypography(currentThemeConfig, baseFontSize),
        },
    });

    useEffect(() => {
        setStoredTheme(isDarkMode ? 'dark' : 'light');
    }, [isDarkMode, setStoredTheme]);

    return (
        <ThemeContext.Provider value={{
            isDarkMode,
            toggleTheme: () => setIsDarkMode(prev => !prev),
            baseFontSize,
            setBaseFontSize
        }}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useCustomTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const getTypography = (themeConfig: any, baseFontSize: number) => {
    const getSize = (variant: string) => {
        const typography = themeConfig?.typography;
        const variantContent = typography?.[variant];
        const fontSize = variantContent?.fontSize;
        if (!fontSize) return undefined;

        const fontSizeStr = fontSize.toString();
        const numericValue = typeof fontSize === 'string' ? parseFloat(fontSizeStr) : fontSize;
        const unit = fontSizeStr.replace(String(numericValue), '').trim();
        const updatedFontSize = numericValue * baseFontSize;

        return {
            ...variantContent,
            fontSize: !!unit ? `${updatedFontSize}${unit}` : updatedFontSize,
        };
    }

    const result = {
        ...themeConfig.typography,
        // htmlFontSize: baseFontSize,
        // fontSize: baseFontSize,
        h1: getSize('h1'),
        h2: getSize('h2'),
        h3: getSize('h3'),
        h4: getSize('h4'),
        h5: getSize('h5'),
        h6: getSize('h6'),
        subtitle1: getSize('subtitle1'),
        subtitle2: getSize('subtitle2'),
        body1: getSize('body1'),
        body2: getSize('body2'),
        caption: getSize('caption'),
        button: getSize('button'),
        overline: getSize('overline'),
    }

    //remove undefined
    return Object.fromEntries(
        Object.entries(result).filter(([_, value]) => value !== undefined)
    );
};